import { createWorker } from "tesseract.js";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

let ocrWorkerPromise = null;

function normalizeText(text = "") {
  return String(text || "")
    .replace(/\r/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

async function getOcrWorker() {
  if (!ocrWorkerPromise) {
    ocrWorkerPromise = (async () => {
      const worker = await createWorker("spa+eng");
      return worker;
    })();
  }
  return ocrWorkerPromise;
}

function isPdfFile(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  return mime.includes("pdf") || name.endsWith(".pdf");
}

async function readPdfTextContent(pdfDocument, maxPages = 4) {
  const totalPages = Math.min(pdfDocument.numPages || 0, maxPages);
  let content = "";
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    const page = await pdfDocument.getPage(pageNumber);
    const textContent = await page.getTextContent();
    const strings = (textContent?.items || []).map((item) => item.str || "");
    content += `\n\n[Pagina ${pageNumber}]\n${strings.join(" ")}`;
  }
  return normalizeText(content);
}

async function ocrPdfPages(pdfDocument, maxPages = 2) {
  const worker = await getOcrWorker();
  const totalPages = Math.min(pdfDocument.numPages || 0, maxPages);
  let ocrText = "";
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    const page = await pdfDocument.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1.8 });
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) continue;

    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    await page.render({ canvasContext: context, viewport }).promise;

    const result = await worker.recognize(canvas);
    const pageText = result?.data?.text || "";
    ocrText += `\n\n[OCR Pagina ${pageNumber}]\n${pageText}`;
  }
  return normalizeText(ocrText);
}

export async function extractTextFromImage(file) {
  const worker = await getOcrWorker();
  const result = await worker.recognize(file);
  const text = normalizeText(result?.data?.text || "");
  return {
    text,
    warnings: text ? [] : ["OCR imagen no detectó texto legible."],
    meta: {
      engine: "tesseract.js",
      source: "image",
      confidence: result?.data?.confidence || null,
    },
  };
}

export async function extractTextFromPdf(file) {
  const buffer = await file.arrayBuffer();
  // Avoid remote worker loading (CSP/offline/CORS issues in browser environments).
  const loadingTask = pdfjsLib.getDocument({ data: buffer, disableWorker: true });
  const pdfDocument = await loadingTask.promise;

  const nativeText = await readPdfTextContent(pdfDocument, 5);
  const ocrText = await ocrPdfPages(pdfDocument, 2);
  const merged = normalizeText(`${nativeText}\n\n${ocrText}`);

  const warnings = [];
  if (!nativeText && !ocrText) {
    warnings.push("PDF sin texto utilizable por OCR/local parsing.");
  }

  return {
    text: merged,
    warnings,
    meta: {
      engine: "pdfjs+tesseract.js",
      source: "pdf",
      pages: pdfDocument.numPages || 0,
    },
  };
}

export async function extractDocumentText(file) {
  if (!file) {
    throw new Error("Archivo requerido para OCR.");
  }

  if (isPdfFile(file)) {
    return extractTextFromPdf(file);
  }

  return extractTextFromImage(file);
}

export function cleanupBrowserOcrWorker() {
  if (!ocrWorkerPromise) return;
  ocrWorkerPromise.then((worker) => worker.terminate()).catch(() => null);
  ocrWorkerPromise = null;
}
